/**
 * Item master pure logic shared by web and mobile: the napkin colour palette
 * (decision C1), the SKU suggestion (E13, still open) and the GTIN check digit
 * that mirrors `private.gtin_ok`.
 *
 * `items.attributes` is free-form JSON on the database side, so everything here
 * reads it defensively — a value written by an import or by an earlier phase may
 * be any shape at all.
 */
import type { ItemStatus, ItemType } from './constants';
import { trLower } from './text';
import { napkinWeightG } from './units';

// ── colours (C1) ────────────────────────────────────────────────────────────
/**
 * `items.attributes.colour` stores the ASCII **key**; the Turkish label and the
 * chip colour are resolved here so tr/en, exports and labels all agree.
 * `hex` mirrors `napkinColors` in `packages/ui-tokens/src/index.ts` — core does
 * not depend on the token package, so the two lists are kept equal by the test
 * in `test/items.test.ts` (update both together).
 * `code` is the SKU abbreviation, confirmed by the owner on 27 Oct 2026 (E13).
 */
export interface ColourSpec {
  /** Turkish label shown on screen and accepted by imports. */
  label: string;
  /** Chip colour, `#RRGGBB` upper case. */
  hex: string;
  /** Three-letter SKU abbreviation (E13, confirmed 27 Oct 2026). */
  code: string;
}

export const COLOURS: Record<string, ColourSpec> = {
  beyaz: { label: 'Beyaz', hex: '#FFFFFF', code: 'BYZ' },
  fildisi: { label: 'Fildişi', hex: '#F2E8D5', code: 'FLD' },
  bej: { label: 'Bej', hex: '#D9C7A7', code: 'BEJ' },
  gri: { label: 'Gri', hex: '#9A9A95', code: 'GRI' },
  altin: { label: 'Altın', hex: '#C9A653', code: 'ALT' },
  lacivert: { label: 'Lacivert', hex: '#1E2A4A', code: 'LAC' },
  siyah: { label: 'Siyah', hex: '#1A1A18', code: 'SYH' },
  terracotta: { label: 'Terracotta', hex: '#B2603C', code: 'TRC' },
  kahve: { label: 'Kahve', hex: '#6B4A2F', code: 'KHV' },
  bebek_mavisi: { label: 'Bebek mavisi', hex: '#AFCBE0', code: 'BMV' },
};

/** Palette order for pickers and filter chips (lightest to darkest, as drawn in W-04). */
export const COLOUR_KEYS: string[] = Object.keys(COLOURS);

const HEX_RE = /^#[0-9A-Fa-f]{6}$/;

function asRecord(attributes: unknown): Record<string, unknown> {
  return attributes && typeof attributes === 'object' && !Array.isArray(attributes) ? (attributes as Record<string, unknown>) : {};
}

function attrString(attributes: unknown, key: string): string | null {
  const v = asRecord(attributes)[key];
  if (typeof v === 'string') return v.trim() || null;
  return null;
}

function attrNumber(attributes: unknown, key: string): number | null {
  const v = asRecord(attributes)[key];
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v))) return Number(v);
  return null;
}

/**
 * Normalises whatever is stored (or typed into an import) into a palette key.
 * Accepts the key itself ("bebek_mavisi"), the Turkish label ("Bebek mavisi",
 * "FİLDİŞİ") and a hyphenated spelling. Returns null when the value is not one
 * of the ten palette colours — a custom colour lives in `chip_hex` instead.
 */
export function colourKey(value: string | null | undefined): string | null {
  if (!value) return null;
  const raw = trLower(String(value)).trim();
  if (!raw) return null;
  const slug = raw
    .replace(/ç/g, 'c')
    .replace(/ğ/g, 'g')
    .replace(/ı/g, 'i')
    .replace(/ö/g, 'o')
    .replace(/ş/g, 's')
    .replace(/ü/g, 'u')
    .replace(/[\s-]+/g, '_');
  return slug in COLOURS ? slug : null;
}

/** "fildisi" → "Fildişi". An unknown value is shown as it was stored, never dropped. */
export function colourLabel(attributes: unknown): string | null {
  const stored = attrString(attributes, 'colour');
  if (!stored) return null;
  const key = colourKey(stored);
  return key ? COLOURS[key]!.label : stored;
}

/**
 * The chip colour of an item (C1). `attributes.chip_hex` wins, so a one-off
 * colour that is not in the palette still gets a chip; otherwise the palette
 * entry decides. Returns null when the item has no colour at all — the caller
 * draws no chip rather than a grey one.
 */
export function itemChipColor(item: { attributes?: unknown } | null | undefined): string | null {
  const attributes = item?.attributes;
  const chip = attrString(attributes, 'chip_hex');
  if (chip && HEX_RE.test(chip)) return chip.toUpperCase();
  const key = colourKey(attrString(attributes, 'colour'));
  return key ? COLOURS[key]!.hex : null;
}

// ── SKU (E13 — pattern not confirmed) ───────────────────────────────────────
/**
 * `save_item` and the import both enforce this shape (decision C42): upper case
 * or digit first, then 1–39 of upper case, digits, dot, underscore or hyphen.
 */
export const SKU_RE = /^[A-Z0-9][A-Z0-9._-]{1,39}$/;

export function isValidSku(sku: string | null | undefined): boolean {
  return !!sku && SKU_RE.test(sku);
}

/**
 * The owner confirmed the pattern on 27 Oct 2026 (E13): the two codes the design
 * draws — `NAP-{ebat}-{katlama}-{renk}` and `BOB-{gsm}-{genişlik}-{renk}` — with
 * the palette's own abbreviations. What is **still** open is narrower: the prefix
 * for yarı mamul, ambalaj, sarf and hizmet, and the abbreviation for fold `flat`
 * and format `dinner`. Those return null rather than an invented code, so a
 * suggestion is always either right or absent. Overrides stay settable here for
 * the day a `settings.item_defaults.sku` key exists.
 */
export interface SkuSuggestionSettings {
  /** Prefix per item type; a type without one gets no suggestion. */
  prefixes?: Partial<Record<ItemType, string>>;
  /** Overrides for the palette's `code` (E13). */
  colourCodes?: Record<string, string>;
}

/** The two confirmed prefixes (E13). The other four types have none yet, so they get no suggestion. */
export const DESIGN_SKU_PREFIXES: Partial<Record<ItemType, string>> = {
  finished_good: 'NAP',
  raw_material: 'BOB',
};

/** `flat` and `dinner` still have no abbreviation (E13), so a SKU simply leaves that segment out. */
const FOLD_CODES: Record<string, string> = { '1/4': '14', '1/6': '16', '1/8': '18', pocket: 'CEP' };
const FORMAT_CODES: Record<string, string> = { cocktail: 'KOK', guest_towel: 'GT' };

function colourCode(attributes: unknown, settings: SkuSuggestionSettings): string | null {
  const stored = attrString(attributes, 'colour');
  const key = colourKey(stored);
  if (key && settings.colourCodes?.[key]) return settings.colourCodes[key]!;
  return key ? COLOURS[key]!.code : null;
}

function intPart(n: number | null): string | null {
  if (n === null) return null;
  const r = Math.round(n);
  return r > 0 ? String(r) : null;
}

/**
 * Suggests a SKU from the item's attributes, following the design samples:
 * `NAP-{w}{h}-{fold|format}-{colour}` and `BOB-{gsm}-{roll_width}-{colour}`.
 * Segments that are not filled in yet are simply left out, and an item type the
 * settings have no prefix for returns null (never a guessed code — E13).
 */
export function suggestSku(type: ItemType, attributes: unknown, settings: SkuSuggestionSettings = {}): string | null {
  const prefix = settings.prefixes?.[type] ?? DESIGN_SKU_PREFIXES[type];
  if (!prefix) return null;
  const parts: string[] = [prefix];
  if (type === 'finished_good' || type === 'semi_finished') {
    const size = asRecord(asRecord(attributes).size_cm);
    const w = intPart(typeof size.w === 'number' ? size.w : null);
    const h = intPart(typeof size.h === 'number' ? size.h : null);
    if (w && h) parts.push(`${w}${h}`);
    const fold = attrString(attributes, 'fold');
    const format = attrString(attributes, 'format');
    const shape = (fold && FOLD_CODES[fold]) ?? (format && FORMAT_CODES[format]) ?? null;
    if (shape) parts.push(shape);
  } else {
    const gsm = intPart(attrNumber(attributes, 'gsm'));
    if (gsm) parts.push(gsm);
    const width = intPart(attrNumber(attributes, 'roll_width_cm'));
    if (width) parts.push(width);
  }
  const colour = colourCode(attributes, settings);
  if (colour) parts.push(colour);
  const sku = parts.join('-');
  return SKU_RE.test(sku) ? sku : null;
}

// ── barcodes ────────────────────────────────────────────────────────────────
/**
 * GTIN-8 / 12 / 13 / 14 check digit. Mirrors `private.gtin_ok`
 * (migration 20260923000001) — both sides must accept exactly the same codes,
 * or a barcode saved by the form would be refused by `set_item_barcode`.
 */
export function gtinOk(code: string | null | undefined): boolean {
  if (!code || !/^\d+$/.test(code)) return false;
  const len = code.length;
  if (len !== 8 && len !== 12 && len !== 13 && len !== 14) return false;
  let sum = 0;
  for (let i = 1; i < len; i++) {
    sum += Number(code[len - 1 - i]) * (i % 2 === 1 ? 3 : 1);
  }
  return (10 - (sum % 10)) % 10 === Number(code[len - 1]);
}

// ── list labels (W-04 "Ebat · kat") ─────────────────────────────────────────
/**
 * How a fold reads inside a list cell. The drawer and the Genel card use the
 * i18n labels (`items.folds.*`); these are the shorter forms the W-04 artboard
 * writes in the "Ebat · kat" column ("40×40 · cepli"), where the word "kat"
 * would repeat the column header.
 */
export const FOLD_LIST_LABELS: Record<string, string> = { '1/4': '1/4', '1/6': '1/6', '1/8': '1/8', pocket: 'cepli', flat: 'düz' };

function numText(n: number): string {
  // 40 → "40", 8.5 → "8,5": list labels are Turkish text, not formatted money
  return String(Math.round(n * 1000) / 1000).replace('.', ',');
}

/**
 * The "Ebat · kat" cell of W-04 and the subtitle of the mobile item card.
 * Napkins get size + fold, bobbins width + gsm, cartons their three dimensions,
 * foil its width in mm. Nothing to say → "—", never an empty cell.
 */
export function itemSizeLabel(type: ItemType | null | undefined, attributes: unknown): string {
  const a = asRecord(attributes);
  const size = asRecord(a.size_cm);
  const dims = asRecord(a.dims_cm);
  const parts: string[] = [];
  if (typeof size.w === 'number' && typeof size.h === 'number') {
    parts.push(`${numText(size.w)}×${numText(size.h)}`);
    const fold = attrString(attributes, 'fold');
    if (fold) parts.push(FOLD_LIST_LABELS[fold] ?? fold);
  } else if (typeof dims.l === 'number' && typeof dims.w === 'number' && typeof dims.h === 'number') {
    parts.push(`${numText(dims.l)}×${numText(dims.w)}×${numText(dims.h)}`);
  } else {
    const roll = attrNumber(attributes, 'roll_width_cm');
    const gsm = attrNumber(attributes, 'gsm');
    const widthMm = attrNumber(attributes, 'width_mm');
    if (roll !== null) parts.push(`${numText(roll)} cm`);
    if (widthMm !== null) parts.push(`${numText(widthMm)} mm`);
    if (gsm !== null && (roll !== null || type === 'raw_material')) parts.push(`${numText(gsm)} g`);
  }
  return parts.length ? parts.join(' · ') : '—';
}

// ── W-04 views, filters and facet counts ────────────────────────────────────
/** Phase 2 has no `saved_views` table: the four design views (+ "Pasif ürünler") live here. */
export const SYSTEM_ITEM_VIEWS = ['all', 'critical', 'on_amazon', 'unmapped', 'inactive'] as const;
export type ItemViewKey = (typeof SYSTEM_ITEM_VIEWS)[number];

/** The fields a view or a filter reads — a subset of `v_items_list`, so the mobile card can filter too. */
export interface ItemViewRow {
  type?: ItemType | null;
  status?: ItemStatus | null;
  /** `v_items_list.stock_status`: normal | low | critical | negative. */
  stock_status?: string | null;
  listing_count?: number | null;
  attributes?: unknown;
}

/**
 * Definitions of the sidebar views. `on_amazon`/`unmapped` stay in the list for
 * Phase 4: `listing_count` is 0 for every item until then, so they simply show
 * their empty state rather than disappearing and moving the other rows.
 */
export function matchesItemView(row: ItemViewRow, view: ItemViewKey): boolean {
  const listings = row.listing_count ?? 0;
  switch (view) {
    case 'critical':
      return row.status !== 'inactive' && (row.stock_status === 'critical' || row.stock_status === 'negative');
    case 'on_amazon':
      return listings > 0;
    case 'unmapped':
      return row.type === 'finished_good' && row.status === 'active' && listings === 0;
    case 'inactive':
      return row.status === 'inactive';
    case 'all':
    default:
      return row.status !== 'inactive';
  }
}

/** Attribute keys the W-04 filter row offers, in the order it draws them. */
export const ITEM_FILTER_KEYS = ['size_cm', 'fold', 'colour', 'print_type', 'gsm'] as const;
export type ItemFilterKey = (typeof ITEM_FILTER_KEYS)[number];

/**
 * One item's value for a filter key, as the filter compares and displays it
 * ("40×40", "1/8", "fildisi", "none", "55"). An item that does not carry the
 * key at all returns null and is filtered out while that key is active (§3.5).
 */
export function itemAttrFilterValue(attributes: unknown, key: ItemFilterKey): string | null {
  if (key === 'size_cm') {
    const size = asRecord(asRecord(attributes).size_cm);
    if (typeof size.w !== 'number' || typeof size.h !== 'number') return null;
    return `${numText(size.w)}×${numText(size.h)}`;
  }
  if (key === 'gsm') {
    const gsm = attrNumber(attributes, 'gsm');
    return gsm === null ? null : numText(gsm);
  }
  if (key === 'colour') {
    const stored = attrString(attributes, 'colour');
    if (!stored) return null;
    return colourKey(stored) ?? stored;
  }
  return attrString(attributes, key);
}

/** key → selected values. Values inside one key are OR, keys are AND. */
export type ItemAttrFilters = Partial<Record<ItemFilterKey, string[]>>;

export function matchesItemAttrs(row: ItemViewRow, attrs: ItemAttrFilters | undefined): boolean {
  if (!attrs) return true;
  for (const [key, values] of Object.entries(attrs) as [ItemFilterKey, string[] | undefined][]) {
    if (!values?.length) continue;
    const v = itemAttrFilterValue(row.attributes, key);
    if (v === null || !values.includes(v)) return false;
  }
  return true;
}

export interface ItemListFilterState {
  view?: ItemViewKey;
  /** undefined = "Tümü". */
  type?: ItemType | null;
  attrs?: ItemAttrFilters;
}

/** View + type + attributes, in the order W-04 narrows them. Text search stays with `matchesSearch`. */
export function applyItemFilters<T extends ItemViewRow>(rows: T[], f: ItemListFilterState = {}): T[] {
  const view = f.view ?? 'all';
  return rows.filter((r) => matchesItemView(r, view) && (!f.type || r.type === f.type) && matchesItemAttrs(r, f.attrs));
}

/**
 * The counts on the type chips. They are computed with every *other* filter
 * applied and the type filter ignored, so clicking a chip never lands on an
 * empty table (§3.4).
 */
export function itemTypeFacets<T extends ItemViewRow>(rows: T[]): { total: number; byType: Record<string, number> } {
  const byType: Record<string, number> = {};
  for (const r of rows) {
    const key = r.type ?? 'unknown';
    byType[key] = (byType[key] ?? 0) + 1;
  }
  return { total: rows.length, byType };
}

/**
 * The values the attribute popover offers for one key: what the currently
 * filtered rows carry, plus whatever is already selected (so a selected value
 * never vanishes from its own popover).
 */
export function itemAttrFacets<T extends ItemViewRow>(rows: T[], key: ItemFilterKey, selected: string[] = []): { value: string; count: number }[] {
  const counts = new Map<string, number>();
  for (const s of selected) counts.set(s, 0);
  for (const r of rows) {
    const v = itemAttrFilterValue(r.attributes, key);
    if (v === null) continue;
    counts.set(v, (counts.get(v) ?? 0) + 1);
  }
  const rank = (v: string) => {
    const i = COLOUR_KEYS.indexOf(v);
    return i < 0 ? COLOUR_KEYS.length : i;
  };
  return [...counts.entries()]
    .map(([value, count]) => ({ value, count }))
    .sort((a, b) => (key === 'colour' ? rank(a.value) - rank(b.value) : 0) || a.value.localeCompare(b.value, 'tr'));
}

// ── unit ladder (W-05 header meta and "Planlama ve paketleme") ──────────────
/** One `item_uoms` row as PostgREST delivers it. */
export interface UomRow {
  unit: string;
  factor_to_base: number | string | null;
  /**
   * False marks a conversion that is not a packaging level — the kg row of a
   * pcs-based napkin exists so a weight can be converted, not because napkins
   * are packed into kilograms. Such rungs stay out of the chain.
   */
  is_countable?: boolean | null;
}

export interface UomStep {
  /** The bigger unit, e.g. "case". */
  unit: string;
  /** The unit it is counted in, e.g. "pack". */
  perUnit: string;
  /** How many `perUnit` go into one `unit` — 16 for "16 paket/koli". */
  qty: number;
}

/**
 * The item's packaging chain, smallest first: `pcs → pack → case → pallet`
 * becomes "50 adet/paket", "16 paket/koli", "40 koli/palet". Each step is
 * counted against the previous rung, which is how the design writes it and how
 * the setup wizard's `caseLine` reads.
 *
 * Rungs whose factor does not divide evenly (a kg row on a pcs-based napkin)
 * are kept with their real quotient; the screen rounds for display.
 */
export function uomLadder(uoms: UomRow[] | null | undefined, baseUnit: string, opts: { skip?: string[] } = {}): UomStep[] {
  const rungs = (uoms ?? [])
    .filter((u) => u.is_countable !== false)
    .map((u) => ({ unit: u.unit, factor: Number(u.factor_to_base ?? 0) }))
    .filter((u) => u.factor > 0 && !opts.skip?.includes(u.unit))
    .sort((a, b) => a.factor - b.factor);
  if (!rungs.some((r) => r.unit === baseUnit)) rungs.unshift({ unit: baseUnit, factor: 1 });
  const steps: UomStep[] = [];
  for (let i = 1; i < rungs.length; i++) {
    const prev = rungs[i - 1]!;
    const cur = rungs[i]!;
    steps.push({ unit: cur.unit, perUnit: prev.unit, qty: Math.round((cur.factor / prev.factor) * 1e6) / 1e6 });
  }
  return steps;
}

// ── the packaging chain a napkin is entered with (W-04b "Birimler ve paketleme") ──
/**
 * What the item form asks for instead of raw factors: how many napkins go in a
 * pack, how many packs in a case, how many cases on a pallet. `settings.packaging`
 * fills the defaults, and an item that is packed differently overrides them.
 */
export interface PackagingChain {
  /** Pieces in one pack. */
  packQty?: number | null;
  /** Packs in one case. */
  casePacks?: number | null;
  /** Cases on one pallet — optional, most items are not palletised in stock. */
  palletCases?: number | null;
  /** One napkin in grams; only used for the kg conversion rung. */
  napkinWeightG?: number | null;
}

/** What the item form knows about a napkin's weight when it builds the chain. */
export interface NapkinWeightInputs {
  /** "Peçete ağırlığı", if the user typed one. */
  typed?: number | null;
  /** Open size in cm, for the gramaj formula. */
  sizeCm?: { w?: number | null; h?: number | null } | null;
  /** `attributes.gsm`. */
  gsm?: number | null;
  /** `settings.packaging.napkin_weight_g`. */
  companyDefault?: number | null;
}

function positive(n: number | null | undefined): number | null {
  return typeof n === 'number' && Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * The weight one napkin is converted with, in the order the cost of being wrong
 * grows: what the user measured, then what the gramaj formula says, then the
 * company default. Only the kg rung of the ladder depends on it — a weight can
 * be converted, but napkins are not packed into kilograms.
 *
 * A weight of zero is not a weight: it falls through to the next source rather
 * than blocking it, and when none of the three is known the caller leaves the
 * kg rung out instead of guessing one.
 */
export function napkinChainWeightG(inputs: NapkinWeightInputs): number | null {
  const typed = positive(inputs.typed);
  if (typed !== null) return typed;
  const w = positive(inputs.sizeCm?.w);
  const h = positive(inputs.sizeCm?.h);
  const gsm = positive(inputs.gsm);
  if (w !== null && h !== null && gsm !== null) return napkinWeightG(w, h, gsm).toNumber();
  return positive(inputs.companyDefault);
}

/** One `item_uoms` row as the form hands it to `save_item`. */
export interface UomLadderInput {
  unit: string;
  /** Decimal text — `save_item` forces the base row to 1 whatever is sent. */
  factorToBase: string;
  isSalesDefault?: boolean;
  isCountable?: boolean;
  sort: number;
}

/** Trims a computed factor to the 6 decimals `item_uoms.factor_to_base` stores. */
function factorText(value: number): string {
  const rounded = Math.round(value * 1e6) / 1e6;
  return String(rounded);
}

/**
 * The UoM ladder of a piece-counted napkin, in the same shape and order that
 * `save_item`'s "napkin" preset builds from `settings.packaging` — the form
 * sends explicit rows so a single item can be packed 40 to a pack while the
 * company default stays 50.
 *
 * A rung whose number is missing is left out rather than guessed, and the kg
 * rung is `is_countable = false`: it exists so a weight can be converted, not
 * because napkins are packed into kilograms (the packaging chain skips it).
 */
export function napkinUomRows(chain: PackagingChain, baseUnit = 'pcs'): UomLadderInput[] {
  const rows: UomLadderInput[] = [{ unit: baseUnit, factorToBase: '1', sort: 0 }];
  const pack = chain.packQty ?? null;
  const casePacks = chain.casePacks ?? null;
  const palletCases = chain.palletCases ?? null;
  const weight = chain.napkinWeightG ?? null;
  if (pack !== null && pack > 0) rows.push({ unit: 'pack', factorToBase: factorText(pack), sort: 1 });
  const caseFactor = pack !== null && pack > 0 && casePacks !== null && casePacks > 0 ? pack * casePacks : null;
  if (caseFactor !== null) rows.push({ unit: 'case', factorToBase: factorText(caseFactor), isSalesDefault: true, sort: 2 });
  if (caseFactor !== null && palletCases !== null && palletCases > 0) {
    rows.push({ unit: 'pallet', factorToBase: factorText(caseFactor * palletCases), sort: 3 });
  }
  if (weight !== null && weight > 0) {
    rows.push({ unit: 'kg', factorToBase: factorText(1000 / weight), isCountable: false, sort: 4 });
  }
  return rows;
}

// ── save_item failures, mapped back to the field that caused them ───────────
/** The form fields `save_item` and its triggers can refuse a value for. */
export type ItemFormField = 'sku' | 'barcode' | 'baseUnit' | 'stockUnit' | 'type' | 'status' | 'isLotTracked' | 'defaultSupplierId';

export interface ItemSaveFieldError {
  field: ItemFormField;
  /**
   * An i18n key when the app words it better than the database does; otherwise
   * undefined and the caller shows the RPC's own Turkish message.
   */
  messageKey?: string;
}

const SAVE_ITEM_HINT_FIELDS: Record<string, ItemSaveFieldError> = {
  sku_format: { field: 'sku', messageKey: 'validation.sku' },
  barcode_invalid: { field: 'barcode', messageKey: 'validation.gtin' },
  barcode_taken: { field: 'barcode' },
  base_unit_locked: { field: 'baseUnit' },
  stock_unit_uom_missing: { field: 'stockUnit' },
  lot_tracking_locked: { field: 'isLotTracked' },
  lot_not_allowed: { field: 'isLotTracked' },
  supplier_type: { field: 'defaultSupplierId' },
  bom_item_type: { field: 'type' },
  component_service: { field: 'type' },
  component_of_active_bom: { field: 'status' },
};

/**
 * Which input a failed `save_item` belongs to, so the drawer marks that field
 * instead of dropping a toast the user has to map back to a field themselves.
 *
 * The RPC's `hint` (migration 20260923000002) answers most cases. A duplicate
 * SKU or barcode is a plain unique-index violation with no hint, so the index
 * name is read from the raw Postgres message — `toAppError` rewrites the
 * message for 23505, which is why the untouched text has to be passed in.
 */
export function itemSaveFieldError(err: { hint?: string | null; message?: string | null } | null | undefined): ItemSaveFieldError | null {
  const byHint = err?.hint ? SAVE_ITEM_HINT_FIELDS[err.hint] : undefined;
  if (byHint) return byHint;
  const message = err?.message ?? '';
  if (message.includes('items_sku_idx')) return { field: 'sku', messageKey: 'validation.skuTaken' };
  if (message.includes('items_barcode_idx')) return { field: 'barcode', messageKey: 'validation.barcodeTaken' };
  return null;
}
